/**
 * File field codec — converts between backend wire format and UploadValue.
 *
 * Used internally by the crud-page factory to auto-inject codecs on
 * `type: 'file'` fields. Not part of the public API surface.
 *
 * @module
 */

/**
 * Parse a single wire value into an UploadValue or null.
 * @param {unknown} value
 * @returns {import('../../contracts/upload.js').UploadValue | null}
 */
function parseSingle(value) {
  if (value == null) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? { kind: 'existing', url: trimmed } : null;
  }

  if (typeof value === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (value);

    // Already an UploadValue — passthrough with validation
    if (obj.kind === 'existing') {
      // Guard: existing must have a non-empty url
      if (typeof obj.url === 'string' && /** @type {string} */ (obj.url).trim()) {
        return /** @type {import('../../contracts/upload.js').UploadValue} */ (value);
      }
      return null;
    }
    if (obj.kind === 'new') {
      return /** @type {import('../../contracts/upload.js').UploadValue} */ (value);
    }

    // Plain object with url (e.g. backend { url, name } or { url, title, description }).
    if (typeof obj.url === 'string') {
      const trimmedUrl = /** @type {string} */ (obj.url).trim();
      if (!trimmedUrl) return null;
      return {
        kind: 'existing',
        url: trimmedUrl,
        ...(typeof obj.name === 'string' ? { name: obj.name } : {}),
        // Photo metadata round-trip (present only for photo fields).
        ...(typeof obj.title === 'string' ? { title: obj.title } : {}),
        ...(typeof obj.description === 'string' ? { description: obj.description } : {}),
        // Internal image-detection signals (never serialized; carried by the picker
        // mapping or a FileMeta-shaped object — map a stray `kind` to `fileKind`).
        ...(typeof obj.mimeType === 'string' ? { mimeType: obj.mimeType } : {}),
        ...(typeof obj.fileKind === 'string' ? { fileKind: /** @type {*} */ (obj.fileKind) }
          : typeof obj.kind === 'string' && obj.kind !== 'existing' && obj.kind !== 'new'
            ? { fileKind: /** @type {*} */ (obj.kind) } : {}),
        ...(typeof obj.extension === 'string' ? { extension: obj.extension } : {}),
      };
    }

    // Object with empty/falsy url — treated as absent
    return null;
  }

  // number, boolean, etc.
  return null;
}

/**
 * Creates a FieldCodec for file fields.
 *
 * - `parse(wireValue)` converts backend strings/objects → UploadValue
 * - `serialize(uiValue)` converts UploadValue → wire format (string URL or null)
 *
 * For multi-file fields, parse returns `UploadValue[]` (nulls filtered out)
 * and serialize returns `string[]` or `null` (empty array → null).
 *
 * `serialize([]) → null`: backends that interpret null as "clear all files"
 * will work. Override with an explicit codec if your endpoint distinguishes
 * `[]` from `null`.
 *
 * @param {boolean} [multiple]
 * @returns {import('../../contracts/forms.js').FieldCodec}
 */
export function createFileCodec(multiple) {
  return {
    /** @param {unknown} wireValue */
    parse(wireValue) {
      if (multiple) {
        if (wireValue == null) return [];
        if (typeof wireValue === 'string') {
          const trimmed = wireValue.trim();
          return trimmed ? [{ kind: 'existing', url: trimmed }] : [];
        }
        if (Array.isArray(wireValue)) {
          return wireValue
            .map((item) => parseSingle(item))
            .filter((v) => v !== null);
        }
        // Single object in multi mode — wrap
        const parsed = parseSingle(wireValue);
        return parsed ? [parsed] : [];
      }

      // Single mode
      if (Array.isArray(wireValue)) {
        // Backend sent array for single field — take first
        return parseSingle(wireValue[0]);
      }
      return parseSingle(wireValue);
    },

    /** @param {unknown} uiValue */
    serialize(uiValue) {
      if (uiValue == null) return null;

      if (Array.isArray(uiValue)) {
        const urls = uiValue
          .map((item) => serializeSingle(item))
          .filter((v) => v !== undefined && v !== '');
        return urls.length ? urls : null;
      }

      return serializeSingle(/** @type {import('../../contracts/upload.js').UploadValue} */ (uiValue));
    },
  };
}

/**
 * Serialize a single UploadValue to wire format.
 *
 * Object-or-string: returns `{ url, title?, description? }` when the value carries a TRIMMED-non-empty
 * `title` or `description` (photo fields), else the bare `url` string (today's shape — metadata-less
 * images stay byte-identical, so existing entities have zero parity change). Internal fields
 * (`mimeType`/`fileKind`/`extension`/`name`) are NEVER serialized.
 *
 * @param {import('../../contracts/upload.js').UploadValue} value
 * @returns {string | { url: string, title?: string, description?: string } | undefined}
 */
function serializeSingle(value) {
  if (value == null) return undefined;
  if (typeof value !== 'object') return undefined;
  if (value.kind === 'new') return undefined; // omit — should not happen when uploadClient is present
  if (value.kind !== 'existing') return undefined;

  const title = typeof value.title === 'string' ? value.title.trim() : '';
  const description = typeof value.description === 'string' ? value.description.trim() : '';
  // Whitespace-only metadata does NOT force an object — keep the bare URL string.
  if (!title && !description) return value.url;

  return {
    url: value.url,
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
  };
}
